/**
 * MODULE: _shared/frontend-shell/src/features/crm/utils/crmAssistantPrefill.ts
 * PURPOSE: Markdown prefill blocks for CRM-scoped AiAssistantPanel opens
 * DEPENDENCIES: ../data/opportunityDrawerOptions, ../hooks/useAccountDrawer, ../hooks/useContact, ../types/opportunity.types, ./crmFormatters
 * PUBLIC_API: buildOpportunityAssistantPrefill, buildContactAssistantPrefill, buildAccountAssistantPrefill, buildDealsPageAssistantPrefill, CRM_ASSISTANT_PROMPTS
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 123/150
 */

import { OPPORTUNITY_STAGE_OPTIONS } from '../data/opportunityDrawerOptions';
import type { AccountDrawerModel } from '../hooks/useAccountDrawer';
import type { ContactDrawerModel } from '../hooks/useContact';
import type { Opportunity } from '../types/opportunity.types';
import { formatCrmCurrency, formatCrmDate } from './crmFormatters';

function stageLabel(stage: string): string {
  return OPPORTUNITY_STAGE_OPTIONS.find((o) => o.value === stage)?.label ?? stage;
}

export function buildOpportunityAssistantPrefill(
  o: Opportunity,
  opts: { lastActivityIso?: string }
): string {
  const lastAct = opts.lastActivityIso?.trim()
    ? formatCrmDate(opts.lastActivityIso)
    : '—';
  const notes = (o.notes ?? '').trim() || '—';
  const amount = formatCrmCurrency(o.amount);
  const closeDate = formatCrmDate(o.closeDateISO);
  return [
    'Context: CRM Opportunity',
    `Name: ${o.name}`,
    `Account: ${o.accountName}`,
    `Stage: ${stageLabel(o.stage)}`,
    `Amount: ${amount}`,
    `Close Date: ${closeDate}`,
    `Last Activity: ${lastAct}`,
    `Notes: ${notes}`,
  ].join('\n');
}

export function opportunityAssistantSuggestedPrompts(primaryContact: string | undefined): string[] {
  const pc = (primaryContact ?? '').trim() || 'the primary contact';
  return [
    'Summarise this deal',
    'What should I prioritise next?',
    `Draft a follow-up email for ${pc}`,
    'What are the risks of this deal slipping?',
  ];
}

export function buildContactAssistantPrefill(c: ContactDrawerModel): string {
  const name = `${c.firstName} ${c.lastName}`.trim() || c.email || '—';
  const tags = (c.tags ?? []).map((t) => t.trim()).filter(Boolean);
  return [
    'Context: CRM Contact',
    `Name: ${name}`,
    `Email: ${c.email || '—'}`,
    `Company: ${c.company || '—'}`,
    `Status: ${c.status}`,
    `Job title: ${(c.jobTitle ?? '').trim() || '—'}`,
    `Tags: ${tags.length ? tags.join(', ') : '—'}`,
    `Notes: ${(c.notes ?? '').trim() || '—'}`,
  ].join('\n');
}

export const CONTACT_ASSISTANT_PROMPTS = [
  "Summarise this contact's engagement history",
  'Draft an outreach email',
  'What deals is this contact involved in?',
] as const;

export function buildAccountAssistantPrefill(
  a: AccountDrawerModel,
  opts: { openDealsCount: number; totalOpenDealValue: number }
): string {
  const total = formatCrmCurrency(opts.totalOpenDealValue);
  return [
    'Context: CRM Account',
    `Name: ${a.name}`,
    `Industry: ${(a.industry ?? '').trim() || '—'}`,
    `Status: ${a.status}`,
    `Territory: ${a.territoryId || '—'}`,
    `Open deals: ${opts.openDealsCount}`,
    `Total open deal value: ${total}`,
    `Notes: ${(a.notes ?? '').trim() || '—'}`,
  ].join('\n');
}

export const ACCOUNT_ASSISTANT_PROMPTS = [
  "Summarise this account's pipeline",
  'Which contacts should I prioritise?',
  'What is the total deal value for this account?',
] as const;

export function buildDealsPageAssistantPrefill(): string {
  return ['Context: CRM Deals page', 'View: All deals hub (table, filters, and KPIs).'].join('\n');
}

export const DEALS_PAGE_ASSISTANT_PROMPTS = [
  'Which deals are at risk of missing this quarter?',
  'Show me deals with no activity in 14+ days',
  'Summarise pipeline health',
] as const;
